import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
} from 'react-router-v7';
import { ReactElement, useState } from 'react';
import { hasEngRoleSelector } from 'selectors/rolesSelectors';
import { useActions } from 'utils/connect';
import { useLoggedInBootstrap } from 'hooks/useLoggedInBootstrap';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import AppSettings from 'settings/AppSettings';
import BulkUserActionsRoute from 'tools/users/BulkUserActionsRoute';
import BusinessContentFormRoute from './business-content/BusinessContentFormRoute';
import BusinessContentRoute from './business-content/BusinessContentRoute';
import ClowncarMenu from 'components/menu/ClowncarMenu';
import ClowncarMenuNavLinkV7 from 'components/menu/ClowncarMenuNavLinkV7';
import JceHomepageGeographyDetailRoute from './jce-geography/JceHomepageGeographyDetailRoute';
import JceHomepageGeographyFormRoute from 'tools/jce-geography/JceHomepageGeographyFormRoute';
import JceHomepageGeographyListRoute from './jce-geography/JceHomepageGeographyListRoute';
import JceVenueCollectionFormRoute from './jce-geography/JceVenueCollectionFormRoute';
import LegalEditorRoute from 'tools/legal/LegalEditorRoute';
import LegalPagesRoute from 'tools/legal/LegalPagesRoute';
import LegalReviewRoute from 'tools/legal/LegalReviewRoute';
import LegalReviewsRoute from 'tools/legal/LegalReviewsRoute';
import PantryLayout from './PantryLayout';
import ResyImporterRoute from './resy-importer/ResyImporterRoute';
import UserDetailsRoute from 'tools/users/UserDetailsRoute';
import UsersRoute from './users/UsersRoute';
import VenueMigrationRoute from './venue-migrations/VenueMigrationRoute';

// Rendered when no v7 route matches. Shows a toast and falls back to /users.
function V7NotFound() {
  const { actions } = useActions();
  const navigate = useNavigate();

  useMountEffect(() => {
    actions.toast.showInfo('This page does not exist. Redirecting home.');
    navigate('/users', { replace: true });
  });

  return null;
}

// Rendered when a v7 route throws an unexpected runtime error.
function V7ErrorBoundary() {
  return (
    <div style={{ padding: '24px' }}>
      An unexpected error occurred. <a href="/">Return to home</a>
    </div>
  );
}

// Restricts a route to engineers when in prod. In non-prod all tool-accessing
// users (incl. Tock employees) may reach it; in prod non-eng users are bounced
// to /users. Currently defensive in prod (the outer SelectRoute gate already
// limits prod to eng), but future-proofs the route if the tool opens to non-eng.
function RequireEngInProd({ children }: { children: ReactElement }) {
  const isEng = useSelector(hasEngRoleSelector);
  const isProd = AppSettings.APP_ENVIRONMENT === 'prod';
  if (isProd && !isEng) {
    return <Navigate to="/users" replace={true} />;
  }
  return children;
}

// ClowncarMenu renders NavLink from react-router-dom (v5), which doesn't work inside a v7
// RouterProvider context — clicking links would update the URL but not trigger v7 re-renders.
// Instead, render the nav links directly using ClowncarMenuNavLinkV7 (react-router-v7 aware).
function V7Sidebar() {
  const showResyImporterNav = AppSettings.APP_ENVIRONMENT !== 'prod';
  const showBulkUserActions =
    useSelector(hasEngRoleSelector) || AppSettings.APP_ENVIRONMENT !== 'prod';

  return (
    <ClowncarMenu sections={{}}>
      <nav className="ClowncarMenu-section">
        {/*
         * Use href (full-page navigation) so clicking "Select app" escapes the v7
         * RouterProvider and lets the v5 SelectRoute render the home page at /.
         */}
        <ClowncarMenuNavLinkV7 variant="primary" text="Select app" href="/" />
      </nav>
      <nav className="ClowncarMenu-section">
        <ClowncarMenuNavLinkV7 variant="primary" text="Users" to="/users" />
        <ClowncarMenuNavLinkV7
          variant="primary"
          text="Users - Bulk Actions"
          to="/users/bulk"
          hide={!showBulkUserActions}
        />
      </nav>
      <nav className="ClowncarMenu-section">
        <ClowncarMenuNavLinkV7
          variant="primary"
          text="JCE Homepage Geography"
          to="/jce-homepage-geography"
        />
        <ClowncarMenuNavLinkV7
          variant="primary"
          text="JCE Business Content"
          to="/jce-business-content"
        />
      </nav>
      {showResyImporterNav && (
        <nav className="ClowncarMenu-section">
          <ClowncarMenuNavLinkV7 variant="primary" text="Resy Importer" to="/resy-importer" />
        </nav>
      )}
      <nav className="ClowncarMenu-section">
        <ClowncarMenuNavLinkV7 variant="primary" text="Venue migration" to="/venue-migrations" />
      </nav>
      <nav className="ClowncarMenu-section">
        <ClowncarMenuNavLinkV7 variant="primary" text="Legal Pages" to="/legal" />
      </nav>
      <nav className="ClowncarMenu-section">
        {/*
         * Use href (full-page navigation) because /logout is handled by the v5 Switch in
         * SelectRoute, outside the v7 RouterProvider — a v7 `to` would fall through to V7NotFound.
         */}
        <ClowncarMenuNavLinkV7 variant="primary" text="Logout" href="/logout" />
      </nav>
    </ClowncarMenu>
  );
}

// Route definitions are module-level (static config only, no router state).
// createBrowserRouter is called inside NewRouterRoot so that it reads window.location
// at component mount time, not at module load time. If it were module-level, it would
// capture the initial URL (e.g. '/') and never update when the v5 router navigates via
// pushState — which does not fire a popstate event that v7 would hear.
const routes = [
  {
    path: '/',
    element: (
      <div className="Clowncar">
        <V7Sidebar />
        <div className="Content" style={{ overflowY: 'auto' }}>
          <Outlet />
        </div>
      </div>
    ),
    errorElement: <V7ErrorBoundary />,
    children: [
      {
        element: <PantryLayout />,
        children: [
          {
            path: 'users',
            children: [
              { index: true, element: <UsersRoute /> },
              {
                path: 'bulk',
                element: (
                  <RequireEngInProd>
                    <BulkUserActionsRoute />
                  </RequireEngInProd>
                ),
              },
              { path: ':id', element: <UserDetailsRoute /> },
            ],
          },
          {
            path: 'jce-homepage-geography',
            children: [
              { index: true, element: <JceHomepageGeographyListRoute /> },
              { path: ':geographyId', element: <JceHomepageGeographyDetailRoute /> },
              { path: 'create', element: <JceHomepageGeographyFormRoute /> },
              { path: ':geographyId/collections/create', element: <JceVenueCollectionFormRoute /> },
              {
                path: ':geographyId/collections/:collectionId/edit',
                element: <JceVenueCollectionFormRoute />,
              },
            ],
          },
          {
            path: 'jce-business-content',
            children: [
              {
                index: true,
                element: <BusinessContentRoute />,
              },
              { path: 'create', element: <BusinessContentFormRoute /> },
              { path: 'tock/:businessId/edit', element: <BusinessContentFormRoute /> },
              { path: 'resy/:venueId/edit', element: <BusinessContentFormRoute /> },
            ],
          },
          {
            path: 'resy-importer',
            element: <ResyImporterRoute />,
          },
          {
            path: 'venue-migrations',
            element: <VenueMigrationRoute />,
          },
          {
            path: 'legal',
            children: [
              { index: true, element: <LegalPagesRoute /> },
              {
                path: ':app/reviews',
                children: [
                  { index: true, element: <LegalReviewsRoute /> },
                  { path: ':workflowId', element: <LegalReviewRoute /> },
                ],
              },
              { path: ':app/:type', element: <LegalEditorRoute /> },
            ],
          },
        ],
      },
      // Catch-all: any unregistered path redirects to /users with a toast
      { path: '*', element: <V7NotFound /> },
    ],
  },
];

export function NewRouterRoot() {
  const [router] = useState(() => createBrowserRouter(routes, { basename: '/' }));
  // Engineers reach the v7 routes via SelectRoute -> NewRouterRoot without mounting
  // TockLoggedInRoute, so run the same logged-in bootstrap (patron profile, role
  // refresh, global features) here too. Without the profile fetch, state.app.patron.email
  // stays empty and email-based access checks (e.g. isLegalEditorSelector) never match.
  useLoggedInBootstrap();
  return <RouterProvider router={router} />;
}
